<template>
  <div
    id="app"
    class="app"
    :class="[
      { 'is-authenticated': isAuthenticated },
      { 'is-not-authenticated': !isAuthenticated },
      { 'is-trial': primaryProposition.isTrial },
      { 'must-select-new-areas': mustSelectNewAreas },
      {
        'is-limited':
          primaryProposition.isFreemium || primaryProposition.isTrial
      }
    ]"
  >
    <a href="#main-content" class="skip-to-content">{{
      $t('skip_to_content')
    }}</a>

    <CookieModal @accepted-all-cookies="initGtm" />

    <DesktopHeader
      v-if="!isMobile && showDesktopHeader && !isAppRequest && !inSignup"
    />

    <DownloadApp v-if="isMobile" />

    <MobileHeader
      v-if="isMobile && showMobileHeader && !isAppRequest && !inSignup"
      :is-authenticated="isAuthenticated"
    />

    <BoostBanner
      v-if="
        primaryProposition.extraVisibility &&
        !primaryPropositionIsPremiumOrFree &&
        $route.name !== 'search'
      "
      class="boost-banner-app"
    />

    <TrialBanner v-if="trialBannerOpen && !inSignup" />

    <MobileMenu
      v-if="menuOpen && isMobile && !isAppRequest"
      :above-tab-bar="tabBarShown"
    />

    <PageModalContainer v-if="pageModalActivePage" />

    <!-- No header: with no title it is just an empty 60px white bar that
         stays fixed above the panel's scrolling content on mobile. -->
    <StandardModal
      v-if="isVisible && proposition"
      standard-modal-name="ExtraVisibilityModal"
      :show-header="false"
      :locked="!!$route.query.q"
      @close="setExtraVisibilityPaymentVisible(false)"
    >
      <ExtraVisibility slot="content" :proposition="proposition" />
    </StandardModal>

    <GlobalModalsContainer v-if="showGlobalModal || endMatchConfirm.show" />

    <transition
      :name="transitionName"
      :mode="transitionMode"
      @after-enter="handleTransitionAfterEnter"
    >
      <router-view
        id="main-content"
        :class="['content', { 'no-tab-bar': !showMobileTabbar }]"
      />
    </transition>

    <TabBar v-if="tabBarShown" />

    <Toast />

    <DevMenu v-if="devMenuEnabled && !isAppRequest" />

    <ImpersonateBanner v-if="isImpersonated && !isMobile"></ImpersonateBanner>

    <NotificationCenterContainer v-if="notificationCenterOpen && isMobile" />

    <Overlay v-if="showOverlay" />
  </div>
</template>

<script>
import { prefetchMapVendor } from '@/utils/prefetchMap';
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';
import Overlay from './components/MobileMenu/Overlay';
import DesktopHeader from './components/DesktopHeader/DesktopHeader';
import MobileMenu from './components/MobileMenu/MobileMenu';
import MobileHeader from './components/MobileMenu/MobileHeader';
import DownloadApp from './components/Banners/DownloadApp';
import TabBar from './components/TabBar';
import TrialBanner from './components/PaymentBanners/TrialBanner';
import Toast from './components/Modals/Toast';
import NotificationCenterContainer from '@/components/NotificationCenter/NotificationCenterContainer';
import CookieModal from './components/CookieModal';
import StandardModal from '@/components/Modals/Standard';
import GlobalModalsContainer from '@/components/GlobalModalsContainer';
import PageModalContainer from '@/components/PageModalContainer';
import BoostBanner from '@/components/Banners/BoostBanner';
import ImpersonateBanner from '@/components/Banners/ImpersonateBanner.vue';
import { isDevMenuEnabled } from '@/utils/devMenu';

import checkAppReady from './store/checkAppReady';

import './scss/_global.scss';
import { addDataLayer } from '@/utils/gtm';
import { getHash256 } from './utils/helpers/hash';
import {
  RATING_MODAL_DELAY_MS,
  markRatingModalSeen,
  shouldShowRatingModal
} from '@/utils/ratingModal';
import { ratingPromptsPaused } from '@/utils/ratingPrompts';
import {
  captureUtmParams,
  getStoredUtmParams,
  clearUtmParams,
  SEND_UTM_CAPTURED_EVENT
} from '@/utils/utm';
import events from '@/utils/helpers/events';
import { applyScrollIntent } from '@/utils/scrollPosition';
import {
  getPendingScrollIntent,
  clearPendingScrollIntent
} from '@/router/router';

const DEFAULT_TRANSITION = 'fade';
const DEFAULT_TRANSITION_MODE = 'out-in';

export default {
  name: 'App',
  components: {
    Overlay,
    DesktopHeader,
    MobileMenu,
    MobileHeader,
    TabBar,
    TrialBanner,
    Toast,
    NotificationCenterContainer,
    CookieModal,
    StandardModal,
    // Async: only rendered inside a v-if-gated modal, and pulls in @adyen
    // (BasePayment → Adyen). Deferring it keeps adyen out of the initial bundle.
    ExtraVisibility: () =>
      import(
        /* webpackChunkName: "extra-visibility" */ '@/components/Payments/ExtraVisibility'
      ),
    GlobalModalsContainer,
    BoostBanner,
    ImpersonateBanner,
    // Always mounted (pageModalActivePage defaults to 'proposition'); it is
    // a thin wrapper that loads the modal's own components, and maplibre with
    // them, only once a proposition modal opens.
    PageModalContainer,
    DownloadApp,
    // Testers only (isDevMenuEnabled), so it is its own chunk that visitors
    // never download.
    DevMenu: () =>
      import(
        /* webpackChunkName: "dev-menu" */ '@/components/DevMenu/DevMenu.vue'
      )
  },

  data() {
    return {
      transitionName: DEFAULT_TRANSITION,
      transitionMode: DEFAULT_TRANSITION_MODE,
      devMenuEnabled: isDevMenuEnabled(),
      // The Trustpilot box is to open (shouldShowRatingModal) and has not yet.
      ratingModalDue: false
    };
  },

  computed: {
    ...mapGetters({
      showGlobalModal: 'ui/showGlobalModal',
      endMatchConfirm: 'ui/endMatchConfirm',
      appReady: 'app/ready',
      user: 'app/user',
      isImpersonated: 'app/isImpersonated',
      isMobile: 'screenSize/isMobile',
      userPropositions: 'myPropositions/propositions',
      accountType: 'myPropositions/accountType',
      totalImageCount: 'myPropositions/totalImageCount',
      showMobileHeader: 'ui/showMobileHeader',
      showDesktopHeader: 'ui/showDesktopHeader',
      showOverlay: 'overlay/showOverlay',
      menuOpen: 'menu/isMenuOpen',
      trialBannerOpen: 'trial/trialBannerOpen',
      tabBarVisible: 'menu/tabBarVisible',
      isAuthenticated: 'app/isAuthenticated',
      isAppRequest: 'app/isAppRequest',
      disableScroll: 'ui/disableScroll',
      notificationCenterOpen: 'menu/isNotificationCenterOpen',
      primaryProposition: 'myPropositions/primaryProposition',
      primaryPropositionId: 'myPropositions/primaryPropositionId',
      primaryPropositionIsPremiumOrFree:
        'myPropositions/primaryPropositionIsPremiumOrFree',
      newMatch: 'matches/newMatch',
      pageModalActivePage: 'ui/pageModalActivePage',
      propositionViewModalId: 'ui/propositionViewModalId',
      inSignup: 'signup/inSignup',
      mustSelectNewAreas: 'wishAreas/mustSelectNewAreas'
    }),

    ...mapState('extraVisibility', ['isVisible', 'proposition']),

    showMobileTabbar() {
      // Hide tab bar if proposition modal is open or swap details is shown
      if (
        this.propositionViewModalId &&
        this.pageModalActivePage === 'proposition'
      ) {
        return false;
      }
      if (this.pageModalActivePage === 'swapdetails') {
        return false;
      }

      const shouldShow = this.isMobile && this.tabBarVisible;
      return shouldShow;
    },

    tabBarShown() {
      return (
        this.isAuthenticated &&
        this.appReady &&
        this.showMobileTabbar &&
        !this.isAppRequest &&
        !this.inSignup &&
        !this.mustSelectNewAreas
      );
    },

    // The Trustpilot box waits while the member finishes their ad or pays
    // (ratingPrompts.js), and never takes the place of another global modal.
    ratingModalPaused() {
      return (
        !!this.showGlobalModal ||
        !!this.endMatchConfirm.show ||
        ratingPromptsPaused({
          route: this.$route,
          boostPanelOpen: this.isVisible
        })
      );
    }
  },

  watch: {
    // Desktop: the swap list route brings maplibre with it, so fetch those
    // chunks while the user is still on whatever page they landed on.
    appReady: {
      immediate: true,
      handler(ready) {
        if (ready && this.isAuthenticated) {
          prefetchMapVendor({ isMobile: this.isMobile });
        }
      }
    },

    disableScroll() {
      document.body.style.overflow = this.disableScroll ? 'hidden' : '';
    },

    ratingModalPaused() {
      this.scheduleRatingModal();
    },

    newMatch(event) {
      // Show full swap modal only if the event was triggered by the current user
      if (
        event &&
        event.triggeredByUser !== undefined &&
        event.triggeredByUser === true
      ) {
        this.setGlobalModal('FullSwapModal');
      }
    }
  },

  async mounted() {
    captureUtmParams();

    this.init();
    this.checkScreenSize();
    this.addDownloadAppMetaTag();

    if (this.isMobile) {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChanged
      );
    }
  },

  created() {
    window.addEventListener('resize', this.checkScreenSize);

    this.$router.beforeEach((to, from, next) => {
      let transitionName = to.meta.transitionName || from.meta.transitionName;
      let transitionMode = 'out-in';

      if (transitionName === 'slide-over') {
        const toDepth = to.path.split('/').length;
        const fromDepth = from.path.split('/').length;
        // Skip slide over right so not to mess with native mobile browsers behaviour
        transitionName = toDepth < fromDepth ? '' : 'slide-over-left';
        transitionMode = '';
      }

      this.transitionName = transitionName || DEFAULT_TRANSITION;
      this.transitionMode = transitionMode;
      next();
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChanged
    );
  },

  methods: {
    ...mapMutations({
      setExtraVisibilityPaymentVisible: 'extraVisibility/setVisible',
      setGlobalModal: 'ui/setGlobalModal'
    }),

    ...mapActions({
      initAppData: 'app/initAppData',
      startMixpanelSession: 'gtm/startMixpanelSession',
      checkScreenSize: 'screenSize/checkScreenSize'
    }),

    /**
     * Handle scroll position restoration after route transition completes.
     * This is called by the <transition> @after-enter hook, ensuring
     * scrolling happens after the new view is fully rendered.
     * Required for iOS Safari compatibility.
     */
    handleTransitionAfterEnter() {
      const intent = getPendingScrollIntent();
      clearPendingScrollIntent();

      // Notify routed views that the enter transition has finished so they can
      // run their own post-transition scroll (iOS Safari only honours scrolls
      // once the transition completes).
      this.$root.$emit('route-after-enter');

      if (!intent || intent.type === 'none') {
        return;
      }

      // Use $nextTick to ensure DOM is fully updated
      this.$nextTick(() => applyScrollIntent(intent));
    },

    handleVisibilityChanged() {
      if (!this.primaryPropositionId) {
        return;
      }

      try {
        const visibilityState =
          document.visibilityState ||
          document.webkitVisibilityState ||
          document.mozVisibilityState ||
          document.msVisibilityState;

        if (visibilityState === 'visible') {
          this.$nextTick(() => {
            this.$store.dispatch(
              'handleSubscription/refreshStates',
              this.primaryPropositionId,
              {
                root: true
              }
            );
          });
        }
      } catch (e) {
        console.log(e.message);
      }
    },

    async init() {
      // Init default consent
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }

      gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied'
      });

      await this.initAppData();

      this.handleUtmTracking();

      const showRatingModal = await shouldShowRatingModal({
        country: this.$country.currentCountry,
        isAuthenticated: this.isAuthenticated,
        registeredAt: this.user.registeredAt
      });
      if (showRatingModal) {
        this.ratingModalDue = true;
        this.scheduleRatingModal();
      }
    },

    // The Trustpilot box opens the whole delay after the member was last
    // busy (ratingModalPaused): being busy stops the clock, and it starts
    // over once they are done.
    scheduleRatingModal() {
      clearTimeout(this.ratingModalTimer);
      if (!this.ratingModalDue || this.ratingModalPaused) return;
      this.ratingModalTimer = setTimeout(() => {
        this.ratingModalDue = false;
        markRatingModalSeen();
        this.setGlobalModal('RatingModal');
      }, RATING_MODAL_DELAY_MS);
    },

    async initGtm() {
      try {
        this.initGTracking();
      } catch (error) {
        console.error(error);
      }
    },

    async initGTracking() {
      await checkAppReady();
      let dataLayerObj = {
        category: 'app',
        action: 'first_load',
        isAuthenticated: this.isAuthenticated ? 1 : 0,
        isMobileUser: this.isMobile
      };

      if (this.isAuthenticated) {
        const active = this.userPropositions.filter(x => x.active);

        dataLayerObj = {
          email: getHash256(this.user.email),
          userId: this.user.userId,
          userCreatedAt: this.user.registeredAt,
          userActiveAds: active.length,
          userNoAdPictures: this.totalImageCount,
          accountType: this.accountType,
          inSwapProcess: active.some(x => x.isSwapProcess),
          isTrial: this.trialBannerOpen,
          ...dataLayerObj
        };
      }
      if (this.isAuthenticated) {
        this.startMixpanelSession(dataLayerObj);
      }
      addDataLayer(dataLayerObj);
    },

    addDownloadAppMetaTag() {
      if (this.primaryPropositionIsPremiumOrFree) {
        const appMetaTag = document.createElement('meta');
        appMetaTag.name = 'apple-itunes-app';
        appMetaTag.content = `app-id=${this.$t('app_ios_id')}`;
        document.head.appendChild(appMetaTag);
      }
    },

    /**
     * Handle UTM parameter tracking
     * - Captures UTM params from URL
     * - If user is authenticated: sends event immediately with userId
     * - If not authenticated: stores UTM for later (sent on login/signup)
     */
    handleUtmTracking() {
      const utmParams = getStoredUtmParams();

      if (!utmParams) {
        return;
      }

      const eventData = {
        ...utmParams,
        referrer: document.referrer
      };

      if (this.isAuthenticated && this.user?.userId) {
        eventData.userId = this.user.userId;
        clearUtmParams();
      }

      if (SEND_UTM_CAPTURED_EVENT) {
        events.emitEvent(events.eventTypes.CLICK, 'UTM Captured', eventData);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.app {
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  color: var(--Text-Primary);
  font-family: Averta, sans-serif;
  height: 100%;
  margin: 0;
  padding: 0;
  position: static;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.boost-banner-app {
  @media ($desktop) {
    position: relative;
    top: 80px;
  }
}

.skip-to-content {
  position: absolute;
  top: -40px;
  left: 0;
  background: #fff;
  padding: 8px;
  z-index: 100;
  transition: top 0.2s;
  font-weight: 700;

  &:focus {
    top: 0;
    outline: 2px solid #000;
  }
}
</style>

<style lang="scss">
// Unscoped so child component styles can override (they have higher specificity with scoped attributes)
.content {
  padding: 0 0 calc(64px + env(safe-area-inset-bottom)) 0;
  transition: padding-top 0.2s ease-in-out;

  &.no-tab-bar {
    padding-bottom: 0;
  }

  @media ($desktop) {
    @include content();
    padding-top: $content-padding-top;
  }
}
</style>
